import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router';
import {useSnackbar} from 'notistack';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {ArrowBack, ContentCopy, Edit} from '@mui/icons-material';
import dayjs from 'dayjs';
import copy from 'copy-to-clipboard';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import oneDark from 'react-syntax-highlighter/dist/esm/styles/prism/one-dark';
import oneLight from 'react-syntax-highlighter/dist/esm/styles/prism/one-light';
import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {EUserGroup, ExampleSnippetDto, GetProductExampleData, ProductExampleStatus, SnippetFormat} from 'apiClient/Api';

const statusChipColor = (status: ProductExampleStatus | undefined): 'success' | 'warning' | 'info' | 'default' => {
    switch (status) {
        case ProductExampleStatus.Approved: return 'success';
        case ProductExampleStatus.Customer: return 'info';
        case ProductExampleStatus.Draft: return 'warning';
        case ProductExampleStatus.Deprecated:
        default: return 'default';
    }
};

/** Maps the snippet format enum to the prism language id used by the highlighter. */
const prismLanguage = (format: SnippetFormat | undefined): string => {
    switch (format) {
        case SnippetFormat.Json: return 'json';
        case SnippetFormat.Yaml: return 'yaml';
        case SnippetFormat.Ini: return 'ini';
        case SnippetFormat.PlainText: return 'text';
        case SnippetFormat.Xml:
        default: return 'markup';
    }
};

const formatLabel = (format: SnippetFormat | undefined, plainTextLabel: string): string => {
    switch (format) {
        case SnippetFormat.Json: return 'JSON';
        case SnippetFormat.Yaml: return 'YAML';
        case SnippetFormat.Ini: return 'INI';
        case SnippetFormat.PlainText: return plainTextLabel;
        case SnippetFormat.Xml:
        default: return 'XML';
    }
};

interface SnippetBlockProps {
    snippet: ExampleSnippetDto;
    index: number;
}

/**
 * Renders a single snippet as an article-style block: heading, description,
 * format chip, code with syntax highlighting and a copy-to-clipboard button.
 */
const SnippetBlock: React.FC<SnippetBlockProps> = ({snippet, index}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const isDark = theme.palette.mode === 'dark';

    const handleCopy = () => {
        if (copy(snippet.content)) {
            enqueueSnackbar(t('productExamples.view.copied'), {variant: 'success'});
        }
    };

    return (
        <Box component="section" sx={{mb: 4}}>
            <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, mb: 1}}>
                <Typography variant="h6" component="h3" sx={{flex: 1, lineHeight: 1.3}}>
                    {index + 1}. {snippet.title}
                </Typography>
                <Chip
                    size="small"
                    label={formatLabel(snippet.format, t('productExamples.format.plainText'))}
                    variant="outlined"
                    sx={{fontFamily: 'monospace', fontWeight: 600}}
                />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5, whiteSpace: 'pre-line'}}>
                {snippet.description}
            </Typography>
            <Box sx={{position: 'relative'}}>
                <Tooltip title={t('productExamples.view.copy')}>
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                            '&:hover': {bgcolor: 'action.hover'},
                        }}
                    >
                        <ContentCopy fontSize="small"/>
                    </IconButton>
                </Tooltip>
                <SyntaxHighlighter
                    language={prismLanguage(snippet.format)}
                    style={isDark ? oneDark : oneLight}
                    customStyle={{
                        margin: 0,
                        borderRadius: 8,
                        padding: '16px 16px 16px 16px',
                        fontSize: '0.85rem',
                        maxHeight: 600,
                        overflow: 'auto',
                    }}
                    showLineNumbers
                    wrapLongLines
                >
                    {snippet.content ?? ''}
                </SyntaxHighlighter>
            </Box>
        </Box>
    );
};

/**
 * Read-only article-style view of a product example. Office users land here
 * by default; users with edit rights see an "Edit" button that takes them to
 * the editor route.
 */
const ProductExampleView: React.FC = () => {
    const {t} = useTranslation();
    useDocumentTitle('productExamples.view.title');
    const context = useApplicationContext();
    const navigate = useNavigate();
    const {id} = useParams<{id: string}>();

    const userGroup = context.userData?.userGroup ?? EUserGroup.CUSTOMER;
    const canEdit = userGroup <= EUserGroup.DEVELOPMENT;

    const [data, setData] = useState<GetProductExampleData | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setLoadError(null);
        try {
            const response = await context.api.productExample.productExampleGet(id);
            setData(response.data);
        } catch {
            setLoadError(t('productExamples.view.loadError'));
        } finally {
            setLoading(false);
        }
    }, [context.api.productExample, id, t]);

    useEffect(() => { load(); }, [load]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 8}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (loadError || !data) {
        return (
            <Box sx={{p: 2}}>
                <Alert severity="error">{loadError ?? t('productExamples.view.loadError')}</Alert>
            </Box>
        );
    }

    const statusKey = ProductExampleStatus[data.status ?? ProductExampleStatus.Draft].toLowerCase();

    return (
        <Box sx={{width: '100%', p: 2, maxWidth: 1100, mx: 'auto'}}>
            <Stack
                direction={{xs: 'column', sm: 'row'}}
                spacing={2}
                sx={{alignItems: {xs: 'stretch', sm: 'center'}, justifyContent: 'space-between', mb: 3}}
            >
                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                    <Tooltip title={t('productExamples.view.back')}>
                        <IconButton onClick={() => navigate('/admin/product-examples')}>
                            <ArrowBack/>
                        </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                        {t('productExamples.view.eyebrow')}
                    </Typography>
                </Stack>
                {canEdit && id && (
                    <Button
                        variant="outlined"
                        startIcon={<Edit/>}
                        onClick={() => navigate(`/admin/product-examples/${id}/edit`)}
                    >
                        {t('productExamples.view.edit')}
                    </Button>
                )}
            </Stack>

            {/* Article header */}
            <Box component="header" sx={{mb: 3}}>
                <Stack direction="row" spacing={1} sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5, mb: 1.5}}>
                    {data.fileTypeName && (
                        <Chip color="primary" variant="outlined" label={data.fileTypeName} size="small"/>
                    )}
                    <Chip
                        size="small"
                        label={t(`productExamples.status.${statusKey}`)}
                        color={statusChipColor(data.status)}
                        sx={{fontWeight: 600}}
                    />
                    {data.targetVersion && (
                        <Chip size="small" variant="outlined" label={`v${data.targetVersion}`}/>
                    )}
                </Stack>

                <Typography variant="h3" component="h1" sx={{lineHeight: 1.15, mb: 1, wordBreak: 'break-word'}}>
                    {data.title}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                    {t('productExamples.view.metaLine', {
                        author: data.updatedBy ?? data.createdBy ?? '—',
                        date: data.updatedAt ? dayjs(data.updatedAt).format('DD.MM.YYYY') : '—',
                    })}
                </Typography>
            </Box>

            {/* Description as lead paragraph */}
            <Typography
                variant="body1"
                sx={{
                    fontSize: '1.05rem',
                    lineHeight: 1.6,
                    color: 'text.primary',
                    mb: 3,
                    whiteSpace: 'pre-line',
                }}
            >
                {data.description}
            </Typography>

            {(data.features?.length ?? 0) > 0 && (
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3}}>
                    {data.features!.map(f => (
                        <Chip key={f} label={f} size="small" variant="outlined"/>
                    ))}
                </Box>
            )}

            <Divider sx={{my: 4}}/>

            {/* Snippets */}
            {(data.snippets?.length ?? 0) === 0 ? (
                <Card variant="outlined">
                    <CardContent>
                        <Typography color="text.secondary" sx={{textAlign: 'center', py: 4}}>
                            {t('productExamples.view.noSnippets')}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Box>
                    <Typography variant="overline" color="text.secondary" sx={{display: 'block', mb: 2, letterSpacing: 1}}>
                        {t('productExamples.view.snippetsHeader', {count: data.snippets!.length})}
                    </Typography>
                    {data.snippets!.map((snippet, idx) => (
                        <SnippetBlock key={snippet.id ?? idx} snippet={snippet} index={idx}/>
                    ))}
                </Box>
            )}

            {/* Internal notes block — only show when present and to internal users */}
            {data.notes && (
                <>
                    <Divider sx={{my: 4}}/>
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{display: 'block', mb: 1, letterSpacing: 1}}>
                            {t('productExamples.view.notes')}
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                borderLeft: 4,
                                borderColor: 'warning.light',
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            <Typography variant="body2">{data.notes}</Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ProductExampleView;
